import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import BoletoClientsAPI from 'dashboard/api/boletoClients';

export function useBoletoClients() {
  const clients = ref([]);
  const stats = ref({
    total_clients: 0,
    active_clients: 0,
    total_files: 0,
    pending_files: 0,
    sent_files: 0,
    failed_files: 0,
    sent_this_month: 0,
  });
  const loading = ref(false);
  const uploading = ref(false);

  // Fetch all clients
  const fetchClients = async () => {
    loading.value = true;
    try {
      const response = await BoletoClientsAPI.get();
      clients.value = response.data;
    } catch (error) {
      useAlert('Erro ao carregar clientes');
      console.error('Error fetching boleto clients:', error);
    } finally {
      loading.value = false;
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await BoletoClientsAPI.getStats();
      stats.value = response.data;
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Create a new client
  const createClient = async clientData => {
    try {
      const response = await BoletoClientsAPI.create(clientData);
      clients.value.push(response.data);
      useAlert('Cliente cadastrado com sucesso');
      await fetchStats();
      return response.data;
    } catch (error) {
      useAlert(error.response?.data?.message || 'Erro ao cadastrar cliente');
      throw error;
    }
  };

  // Update a client
  const updateClient = async (clientId, clientData) => {
    try {
      const response = await BoletoClientsAPI.update(clientId, clientData);
      const index = clients.value.findIndex(c => c.id === clientId);
      if (index !== -1) {
        clients.value[index] = response.data;
      }
      useAlert('Cliente atualizado com sucesso');
      return response.data;
    } catch (error) {
      useAlert(error.response?.data?.message || 'Erro ao atualizar cliente');
      throw error;
    }
  };

  // Delete a client
  const deleteClient = async clientId => {
    try {
      await BoletoClientsAPI.delete(clientId);
      clients.value = clients.value.filter(c => c.id !== clientId);
      useAlert('Cliente removido com sucesso');
      await fetchStats();
    } catch (error) {
      useAlert(error.response?.data?.message || 'Erro ao remover cliente');
      throw error;
    }
  };

  // Send boleto now (requires boletoFileId)
  const sendNow = async (clientId, boletoFileId) => {
    try {
      const response = await BoletoClientsAPI.sendNow(clientId, boletoFileId);
      const data = response.data;
      
      if (data.remaining_sends > 0) {
        useAlert(`Boleto enviado com sucesso! (${data.times_sent} de ${data.times_sent + data.remaining_sends} envios)`);
      } else {
        useAlert('Boleto enviado com sucesso! (todos os envios concluídos)');
      }
      
      await fetchClients();
      await fetchStats();
      return data;
    } catch (error) {
      useAlert(error.response?.data?.error || 'Erro ao enviar boleto');
      throw error;
    }
  };

  // Upload files for a client
  const uploadFiles = async (clientId, files, monthMappings = {}) => {
    uploading.value = true;
    try {
      const formData = new FormData();

      files.forEach((file, index) => {
        formData.append(`files[${index}][file]`, file);
        if (monthMappings[file.name]) {
          formData.append(
            `files[${index}][reference_month]`,
            monthMappings[file.name]
          );
        }
      });

      console.log('[BoletoUpload] Sending', files.length, 'files');

      const response = await BoletoClientsAPI.bulkCreateFiles(
        clientId,
        formData
      );

      console.log('[BoletoUpload] Response:', response.data);

      if (response.data.success) {
        useAlert(
          `${response.data.created} arquivo(s) enviado(s) com sucesso`
        );
      } else {
        const errorDetails = response.data.error_details || response.data.error || 'Erro desconhecido';
        useAlert(
          `${response.data.created || 0} enviado(s), ${response.data.failed || 0} com erro: ${errorDetails}`
        );
      }

      await fetchClients();
      await fetchStats();
      return response.data;
    } catch (error) {
      console.error('[BoletoUpload] Error:', error.response?.data || error);
      
      // Extract detailed error message
      const errorData = error.response?.data;
      let errorMessage = 'Erro ao enviar arquivos';
      
      if (errorData) {
        if (errorData.error_details) {
          errorMessage = errorData.error_details;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.results) {
          const failures = errorData.results.filter(r => !r.success);
          if (failures.length > 0) {
            errorMessage = failures.map(f => `${f.filename}: ${f.error}`).join('; ');
          }
        }
      }
      
      useAlert(errorMessage);
      throw error;
    } finally {
      uploading.value = false;
    }
  };

  // Upload single file
  const uploadFile = async (clientId, file, referenceMonth, amount, dueDate) => {
    uploading.value = true;
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (referenceMonth) {
        formData.append('reference_month', referenceMonth);
      }
      if (amount) {
        formData.append('amount', amount);
      }
      if (dueDate) {
        formData.append('due_date', dueDate);
      }

      const response = await BoletoClientsAPI.createFile(clientId, formData);
      useAlert('Arquivo enviado com sucesso');
      await fetchClients();
      await fetchStats();
      return response.data;
    } catch (error) {
      useAlert(error.response?.data?.errors?.[0] || 'Erro ao enviar arquivo');
      throw error;
    } finally {
      uploading.value = false;
    }
  };

  // Delete file
  const deleteFile = async (clientId, fileId) => {
    try {
      await BoletoClientsAPI.deleteFile(clientId, fileId);
      useAlert('Arquivo removido com sucesso');
      await fetchClients();
      await fetchStats();
    } catch (error) {
      useAlert(error.response?.data?.message || 'Erro ao remover arquivo');
      throw error;
    }
  };

  // Retry failed file
  const retryFile = async (clientId, fileId) => {
    try {
      const response = await BoletoClientsAPI.retryFile(clientId, fileId);
      useAlert('Reenvio realizado com sucesso');
      await fetchClients();
      await fetchStats();
      return response.data;
    } catch (error) {
      useAlert(error.response?.data?.error || 'Erro ao reenviar boleto');
      throw error;
    }
  };

  // Detect month from file
  const detectMonth = async (clientId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await BoletoClientsAPI.detectMonth(clientId, formData);
      return response.data;
    } catch {
      return null;
    }
  };

  return {
    clients,
    stats,
    loading,
    uploading,
    fetchClients,
    fetchStats,
    createClient,
    updateClient,
    deleteClient,
    sendNow,
    uploadFiles,
    uploadFile,
    deleteFile,
    retryFile,
    detectMonth,
  };
}
